import React from 'react'

import { UseFormReturn } from 'react-hook-form'

import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'

import { MFAError, decodeToken, useCredentials } from '@dentalux/security'

import AuthLayout from 'src/components/AuthLayout'
import { DialogNames } from 'src/components/dialogs/DialogRoot'
import { OTPFields } from 'src/components/inputs/OtpInput/OtpInput'
import { useDialog } from 'src/components/providers/DialogProvider'
import usePrepareErrorMessage from 'src/hooks/usePrepareErrorMessage'

import MFAForm from './components/MFAForm'
import { SubHeading } from './components/SubHeading/SubHeading'
import * as S from './MFA.styles'

const SentryErrorBoundary = dynamic(() => import('@sentry/react').then((mod) => mod.ErrorBoundary))

const MFAScreen = ({
  form,
  error,
  isLoading,
  onSubmit,
  deviceSavingPossible,
}: {
  form: UseFormReturn<OTPFields>
  error: MFAError
  isLoading: boolean
  deviceSavingPossible: boolean
  onSubmit: (data: OTPFields) => void
}) => {
  const { t } = useTranslation()

  const { token } = useCredentials()

  const { openDialog } = useDialog()

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = form

  const errorMessage = usePrepareErrorMessage(error)

  return (() => {
    if (!token) return null

    const decodedToken = decodeToken(token)

    const number = decodedToken?.['custom.obfuscatedPhoneNumber'] ?? ''

    const partial = decodedToken?.['partial'] ?? false

    if (!partial) return null

    return (
      <AuthLayout
        heading={t('b2c.mfa.heading')}
        subHeading={
          <SentryErrorBoundary fallback={<div />}>
            <SubHeading number={number} />
          </SentryErrorBoundary>
        }
        extra={
          <S.SendPhoneResetEmail
            onClick={() => {
              openDialog(DialogNames.ResetPhoneNumber, { email: decodedToken['sub'] })
            }}
          >
            {t('b2c.mfa.question')}
          </S.SendPhoneResetEmail>
        }
        error={errorMessage}
      >
        <MFAForm
          deviceSavingPossible={deviceSavingPossible}
          values={{ code: watch('code'), rememberDevice: watch('rememberDevice') }}
          handleSubmit={handleSubmit}
          control={control}
          isValid={isValid}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />
      </AuthLayout>
    )
  })()
}

export default MFAScreen
